import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight, ComparisonTable } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-cash-out-refinance";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "DSCR cash out refinance",
  "cash out refinance rental property",
  "DSCR refinance",
  "investment property cash out refinance",
  "BRRRR refinance loan",
  "cash out refinance no income verification",
  "DSCR cash out LTV",
  "rental property equity loan",
  "cash out refinance LLC property",
  "DSCR refinance rates",
  "delayed financing rental property",
  "cash out refinance seasoning requirement",
  "DSCR cash out refinance Florida",
].join(", ");

const FAQS = [
  {
    q: "How much can I cash out of a rental property with a DSCR loan?",
    a: "Cash-out generally maxes at 75% LTV at the strongest tier, and lower at reduced credit scores, higher loan amounts, or on short-term rentals. Your net proceeds are that amount less the existing payoff and closing costs.",
  },
  {
    q: "Is there a seasoning requirement?",
    a: "Under 180 days of ownership, value is typically the lesser of purchase price or appraised value. Appraised value can be used at 90–180 days with documented completed improvements, a loan at or under 120% of cost basis, a minimum one-year prepayment penalty, a tenant in place, and DSCR of at least 1.15.",
  },
  {
    q: "Do I need income documentation to cash out?",
    a: "No. The refinance is underwritten on the property's rent against the new PITIA. No tax returns, W-2s, or DTI calculation.",
  },
  {
    q: "Can I cash out of a property held in an LLC?",
    a: "Yes — entity vesting is standard on DSCR and carries no pricing premium. We will need the entity documents and a guarantor.",
  },
  {
    q: "What can I use the proceeds for?",
    a: "Business purposes only: acquiring another investment property, funding rehab, paying off business debt, or reserves for your portfolio. These loans may not be used for personal, family, or household purposes.",
  },
  {
    q: "How is this different from a rate-and-term refinance?",
    a: "Rate-and-term only replaces the existing balance plus limited costs and allows higher leverage and better pricing. Cash-out takes equity out, caps lower, and prices slightly higher.",
  },
  {
    q: "Can I do a cash-out refinance on a property I own free and clear?",
    a: "Yes. A property with no existing mortgage still follows the same cash-out leverage caps — generally up to 75% LTV at the strongest tier — and ownership seasoning rules apply the same way as on a mortgaged property.",
  },
  {
    q: "Does a cash-out DSCR refinance require an appraisal?",
    a: "Yes, an appraisal is required to establish current value and support the rent used in the DSCR calculation. Value is also compared to seasoning rules if the property was acquired recently.",
  },
  {
    q: "Can I cash out on a short-term rental?",
    a: "Yes on select programs, though short-term rental cash-out generally caps lower than a standard long-term rental and is underwritten on documented platform revenue or a market income projection.",
  },
  {
    q: "How does DSCR affect my maximum cash-out amount?",
    a: "A stronger coverage ratio at the resulting loan amount generally supports higher leverage within program caps, while a marginal ratio may require pulling less cash out or accepting a lower LTV to keep the file within guidelines.",
  },
  {
    q: "Are closing costs higher on a cash-out refinance than a purchase?",
    a: "Closing costs are generally similar in structure — title, escrow, appraisal, and lender fees — though cash-out pricing itself typically carries a modest rate adjustment compared to a purchase or rate-and-term transaction.",
  },
  {
    q: "Can I use cash-out proceeds as the down payment on another property?",
    a: "Yes, this is a common business use of proceeds. Funds are disbursed to you at closing and there is no restriction tying them to a specific future purchase, as long as the use remains business purpose.",
  },
];

export const Route = createFileRoute("/dscr-cash-out-refinance")({
  head: () =>
    buildHead({
      title: "DSCR Cash-Out Refinance: Pull Equity From Rentals, No Tax Returns",
      description:
        "Cash-out refinance an investment property on rental income alone — up to 75% LTV, LLC vesting, no income docs. Fund the next deal with equity you already own.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Cash-Out Refinance", url: URL },
      ],
      service: { name: "DSCR Cash-Out Refinance" },
      faqs: FAQS,
    }),
  component: CashOut,
});

function CashOut() {
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR Cash-Out">
      <PageHero
        eyebrow="DSCR · Cash-Out Refinance"
        title="DSCR cash-out refinance"
        subtitle="Turn trapped rental equity into buying power. The property's rent qualifies the new loan — not your tax returns — so the equity you have built can fund the next acquisition instead of sitting idle."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <PageEssentials hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Cash-Out Refinance" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Snapshot" },
          { href: "#worked-example", label: "Worked example" },
          { href: "#use-cases", label: "Use cases" },
          { href: "#seasoning", label: "Seasoning rules" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "A DSCR cash-out refinance pulls equity out of a rental using the property's cash flow, with no personal income documentation.",
          "Cash-out is generally capped at 75% LTV, a tier below rate & term, and prices modestly higher.",
          "Seasoning rules decide whether the lender uses your purchase price or the new appraised value — this is the single biggest variable on BRRRR deals.",
          "Proceeds are commonly recycled into the next down payment, rehab, or to pay off hard money before it matures.",
          "Figures shown are illustrative; your DSCR, appraisal, and reserves determine actual proceeds.",
        ]}
      />

      <Section id="snapshot">
        <StatRow
          items={[
            { label: "Max LTV", value: "Up to 75%" },
            { label: "Min FICO", value: "660" },
            { label: "Min DSCR", value: "1.00 typical" },
            { label: "Loan size", value: "$100k – $3.5MM" },
          ]}
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          A DSCR cash-out refinance replaces your existing loan with a larger one and returns the difference to you at
          closing. Qualification is the same ratio test as a purchase — gross rent divided by the new PITIA — which is
          why self-employed and portfolio investors use it instead of a conventional cash-out.
        </p>
      </Section>

      <ComparisonTable
        productLabel="DSCR cash-out"
        comparison={{
          intro: "Cash-out versus rate-and-term and a HELOC on a rental. General program ranges only — not an offer or commitment to lend.",
          columns: ["DSCR Cash-Out", "DSCR Rate-and-Term", "Investment HELOC"],
          rows: [
            { feature: "Cash to you", values: ["Yes — equity out at closing", "Minimal / none", "Draw as needed"] },
            { feature: "Max LTV (typical)", values: ["Up to 75%", "Up to 80%", "Lower, lender-specific"] },
            { feature: "Income docs", values: ["None — rent qualifies", "None — rent qualifies", "Often required"] },
            { feature: "Replaces first mortgage", values: ["Yes", "Yes", "No — second lien"] },
            { feature: "Pricing", values: ["Modest cash-out adjustment", "Best DSCR pricing", "Usually variable"] },
            { feature: "Best for", values: ["Funding the next purchase", "Lowering rate or term", "Flexible small draws"] },
          ],
        }}
      />

      <div className="bg-muted/40">
        <Section id="worked-example">
          <SectionHeader eyebrow="The math" title="What a cash-out actually nets you" intro="Illustrative example only — your figures will differ." />
          <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {[
                  ["Appraised value", "$420,000"],
                  ["Max cash-out LTV (75%)", "$315,000"],
                  ["Existing loan payoff", "$212,000"],
                  ["Estimated closing costs & escrows", "$11,500"],
                  ["Estimated cash to borrower", "$91,500"],
                  ["Market rent", "$2,950 / mo"],
                  ["New PITIA @ illustrative pricing", "$2,610 / mo"],
                  ["Resulting DSCR", "1.13 — qualifies"],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td className="px-4 py-3 text-muted-foreground">{label}</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Illustrative only. Not a quote, offer, or commitment to lend. Actual proceeds depend on appraised value,
            payoff, credit tier, DSCR, and closing costs.
          </p>
        </Section>
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <LiveDscrRateSection
            strategyCards
        scenarioOverrides={{ loanPurpose: "cash_out_refinance" }}
        toolLabel="DSCR cash-out refinance"
        id="live-rates"
        eyebrow={"Live pricing · Cash-out"}
        heading={"Current DSCR Cash-Out Refinance Rates"}
        paragraphs={[
            "Cash-out refinances are priced separately from purchase and rate-and-term scenarios, and pricing differences between those purposes vary by investor and program. The live options below come from a real pricing search under our production pricing context, so the rate, APR, points or lender credit and payment on each row all come from the same quote.",
            "Leverage is a central input on a cash-out file, and maximum LTV and any leverage-based pricing adjustments vary by investor and program. Because the loan amount is set by appraised value rather than a contract price, an appraisal that comes in short can change both your proceeds and the options available.",
            "Use of funds affects eligibility: these are business-purpose loans, so proceeds may not be used for personal, family or household purposes.",
        ]}
        faqs={[
            { q: "Why is my DSCR cash-out rate higher than a purchase rate?", a: "Loan purpose is one of the inputs investors price on, and many programs price a cash-out differently from a purchase. Whether an adjustment applies to your file, and how large it is, is program-specific and shows up in the live quote for your scenario." },
            { q: "How much equity do I need to leave in the property?", a: "Maximum cash-out leverage varies by investor, property type and borrower profile, so there is no single figure we can publish. We can confirm the limits available for your scenario in writing." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "Price a DSCR cash-out scenario in the rate center" },
            { to: "/dscr-rate-term-refinance", label: "Compare with DSCR rate & term refinance" },
            { to: "/dscr-heloc", label: "Consider an investment property HELOC instead" },
        ]}
      />

      <Section id="use-cases">
        <SectionHeader eyebrow="Why investors use it" title="Common cash-out scenarios" />
        <div className="mt-8">
          <FeatureGrid
            items={[
              { title: "BRRRR exit", body: "Refinance out of hard money after the rehab is complete and the property is leased, recycling your capital into the next deal." },
              { title: "Portfolio expansion", body: "Convert equity in a seasoned rental into the down payment on one or two additional doors." },
              { title: "Rehab funding", body: "Pull cash for capital improvements on other properties in your portfolio without a construction loan." },
              { title: "Debt consolidation", body: "Retire higher-cost business debt or a maturing balloon with 30-year fixed rental financing." },
            ]}
          />
        </div>
      </Section>

      <Section id="seasoning">
        <SectionHeader eyebrow="Timing rules" title="Seasoning and value: what counts, and when" />
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            ["Under 90 days owned", "Value is generally the lesser of purchase price or appraised value. Delayed-financing style structures may apply."],
            ["90–180 days owned", "Appraised value can be used with documented completed improvements, the loan at or under 120% of cost basis, a one-year minimum prepay, a tenant in place, and DSCR of at least 1.15."],
            ["Over 180 days owned", "Full appraised value applies with no cost-basis limitation — the standard BRRRR refinance window."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-5">
              <div className="font-display text-base font-semibold text-foreground">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <ProsCons
        title="Cash-out refinance: benefits and trade-offs"
        pros={[
          "Access equity without selling or triggering a taxable sale",
          "No income documentation and no DTI test",
          "Close in your LLC with no pricing premium",
          "30-year fixed and interest-only structures available",
        ]}
        cons={[
          "Leverage caps below purchase — generally 75% LTV",
          "Rate prices slightly above rate-and-term",
          "A larger loan means a higher PITIA and a lower DSCR",
          "Prepayment penalties apply on best-priced options",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Find out how much equity you can access"
        body="Send the address, current rent, and payoff balance. We'll run the cash-out scenario and show you the number."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Cash-out refinance questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Business-purpose loans only; proceeds may not be used for personal, family, or household purposes. Figures
          shown are illustrative, subject to change, and are not a quote, offer, or commitment to lend. Subject to
          underwriting and approval.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-cash-out" />

      <CTABand
        title="Put your rental equity to work"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
